/**
 * UserPermissionsSection — Iter 418.l (Dirk, 2026-02-28).
 *
 * Self-contained per-user permission editor. Dropped into
 * `MemberAdminPanel` via its `permissionsSlot` prop from
 * `Settings → Team` so owners can toggle module access per user
 * from the place they'd naturally look for it (previously the
 * feature only existed at `/tools/users-roles`, which Dirk
 * couldn't find).
 *
 * Reads the catalog from `GET /admin/permissions/catalog` and
 * the roles from `GET /admin/roles`, then computes:
 *   effective[key] = override[key] ?? roleDefault[key]
 * and saves via `PATCH /admin/users/{uid}/permissions`.
 *
 * Owner-only (matches backend gate).
 */
import { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { Shield, Edit3, Save, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const GROUP_META = {
  nav:         { label: "Navigation",         color: "#1FB6C1" },
  tools:       { label: "Tools",              color: "#A78BFA" },
  customers:   { label: "Customers & Leads",  color: "#EAB308" },
  jobcards:    { label: "Job Cards & Tickets",color: "#F59E0B" },
  stockroom:   { label: "Stockroom & Inventory", color: "#10B981" },
  picking:     { label: "Picking Slips",      color: "#22D3EE" },
  suppliers:   { label: "Suppliers",          color: "#F97316" },
  procurement: { label: "Procurement",        color: "#EF4444" },
  bookkeep:    { label: "Bookkeep & Invoicing", color: "#84CC16" },
  teams:       { label: "Teams & Chat",       color: "#8B5CF6" },
  performance: { label: "Performance Module", color: "#0EA5E9" },
  projects:    { label: "Projects",           color: "#6366F1" },
  trips:       { label: "Trips",              color: "#F472B6" },
  ai:          { label: "AI / Hidro",         color: "#EC4899" },
  data:        { label: "Data & Export",      color: "#14B8A6" },
  settings:    { label: "Settings",           color: "#94A3B8" },
  action:      { label: "Data actions",       color: "#F59E0B" },  // legacy fallback
};

export default function UserPermissionsSection({ user, meRole, onChanged }) {
  const [catalog, setCatalog] = useState(null);   // {keys:[{key,label,group}], role_defaults:{}}
  const [roles, setRoles] = useState(null);       // [{role, permissions:{...}}]
  const [override, setOverride] = useState(() => ({ ...(user.permissions_override || {}) }));
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setOverride({ ...(user.permissions_override || {}) }); }, [user]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [cat, rs] = await Promise.all([
          api.get("/admin/permissions/catalog"),
          api.get("/admin/roles"),
        ]);
        if (!alive) return;
        setCatalog(cat.data);
        setRoles(rs.data);
      } catch (e) {
        // 403 for non-owners — silent, section just doesn't render.
      }
    })();
    return () => { alive = false; };
  }, []);

  const groups = useMemo(() => {
    if (!catalog) return null;
    const out = {};
    for (const k of catalog.keys) {
      (out[k.group] ||= []).push(k);
    }
    return out;
  }, [catalog]);

  const roleDefaults = useMemo(() => {
    if (!roles) return {};
    const r = roles.find((x) => x.role === user.role);
    return (r && r.permissions) || {};
  }, [roles, user.role]);

  const effective = useMemo(() => ({ ...roleDefaults, ...override }), [roleDefaults, override]);
  const overrideCount = Object.keys(override).length;

  // Owner rows and non-owners can't customise, matches backend gate.
  if (meRole !== "owner") return null;
  if (user.role === "owner") return null;

  if (!catalog || !groups) {
    return (
      <div className="hv-card p-4 flex items-center gap-2 text-xs text-[#a0aabf]">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading permission catalog…
      </div>
    );
  }

  const toggle = (key) => {
    if (!editing) return;
    setOverride((prev) => {
      const next = { ...prev };
      const defaultVal = !!roleDefaults[key];
      if (!(key in next)) {
        next[key] = !defaultVal;
      } else if (next[key] === !defaultVal) {
        delete next[key];
      } else {
        next[key] = !defaultVal;
      }
      return next;
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/users/${user.id}/permissions`, {
        permissions_override: override,
      });
      toast.success("Permissions saved.");
      setEditing(false);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save permissions.");
    } finally { setBusy(false); }
  };

  return (
    <div className="hv-card p-4 space-y-3" data-testid={`user-perm-${user.id}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs font-bold uppercase tracking-wider text-[#cbd2df] inline-flex items-center gap-1.5">
          <Shield className="w-3.5 h-3.5 text-[#1FB6C1]" /> Permissions
          <span className="text-[10px] text-[#647087] normal-case ml-1">
            role: {user.role}
            {overrideCount > 0 ? ` · ${overrideCount} override${overrideCount === 1 ? "" : "s"}` : " · using role defaults"}
          </span>
        </div>
        {!editing ? (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="hv-btn hv-btn-primary text-xs"
            data-testid={`user-perm-edit-${user.id}`}
          >
            <Edit3 className="w-3 h-3" /> Edit permissions
          </button>
        ) : (
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={save}
              disabled={busy}
              className="hv-btn hv-btn-primary text-xs disabled:opacity-50"
              data-testid={`user-perm-save-${user.id}`}
            >
              <Save className="w-3 h-3" /> {busy ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              onClick={() => { setOverride({ ...(user.permissions_override || {}) }); setEditing(false); }}
              className="hv-btn hv-btn-ghost text-xs"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => setOverride({})}
              className="hv-btn hv-btn-ghost text-xs"
              title="Remove all per-user overrides — user falls back to role defaults"
            >
              Clear overrides
            </button>
          </div>
        )}
      </div>

      {editing && (
        <div className="text-[11px] text-[#a0aabf]">
          Tap a chip to toggle. Chips with a coloured dot (●) have a per-user override on top of the role default.
        </div>
      )}

      <div className="space-y-4">
        {Object.entries(groups).map(([gKey, items]) => {
          const meta = GROUP_META[gKey] || { label: gKey, color: "#a0aabf" };
          return (
            <div key={gKey}>
              <div className="text-[10px] uppercase tracking-wider font-bold mb-2" style={{ color: meta.color }}>
                {meta.label}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {items.map((k) => {
                  const active = !!effective[k.key];
                  const hasOverride = k.key in override;
                  return (
                    <button
                      key={k.key}
                      type="button"
                      disabled={!editing}
                      onClick={() => toggle(k.key)}
                      title={`${k.key}${hasOverride ? " · per-user override" : ""}`}
                      data-testid={`user-perm-chip-${user.id}-${k.key}`}
                      className={`relative text-[10px] uppercase tracking-wider font-mono px-2.5 py-1.5 rounded border inline-flex items-center gap-1 transition ${editing ? "cursor-pointer hover:scale-[1.03]" : "cursor-default"} ${active ? "" : "opacity-40"}`}
                      style={{
                        borderColor: active ? `${meta.color}aa` : "rgba(255,255,255,0.08)",
                        background: active ? `${meta.color}14` : "rgba(255,255,255,0.02)",
                        color: active ? meta.color : "#647087",
                      }}
                    >
                      {active ? <Check className="w-2.5 h-2.5" /> : <X className="w-2.5 h-2.5" />}
                      {k.label}
                      {hasOverride && (
                        <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full" style={{ background: meta.color, boxShadow: `0 0 6px ${meta.color}` }} />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
